<app-normal-navbar></app-normal-navbar>

<div class="site-page-wrapper">
  <!-- Soft Ambient Glow Layers -->
  <div class="site-ambient-bg"></div>

  <!-- Standardized Centered Container -->
  <div class="site-container">

    <!-- ═══════════════ HERO SECTION ═══════════════ -->
    <header class="hero-section">

      <h1 class="hero-title">About SceneTale</h1>

      <p class="hero-subtitle">
        SceneTale is an immersive cinematic destination created to celebrate the art of storytelling. We combine
        real-time film databases, thought-provoking editorial articles, and interactive trivia challenges into one
        seamless experience.
      </p>

      <div class="hero-meta-row">
        <span class="meta-pill"><strong>Founded:</strong> 2025</span>
        <span class="meta-pill"><strong>Mission:</strong> Film Discovery &amp; Trivia</span>
        <span class="meta-pill"><strong>Powered By:</strong> TMDB &amp; Community</span>
      </div>
    </header>

    <!-- ═══════════════ THREE CORE PILLARS ═══════════════ -->
    <div class="policy-sections-list">

      <!-- Section 1: The Three Pillars -->
      <section class="policy-card">
        <div class="card-header-row">
          <div class="card-icon-box icon-emerald">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
              <polyline points="2 17 12 22 22 17"></polyline>
              <polyline points="2 12 12 17 22 12"></polyline>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Core Ecosystem</span>
            <h2 class="card-title">Three Pillars of SceneTale</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            Whether you are searching for tonight's movie recommendation, reading an analytical piece on Hitchcock's
            visual framing, or competing against film buffs across the globe, SceneTale has you covered:
          </p>
          <div class="grid-two-col">
            <div class="feature-box">
              <h4>🎬 01. Comprehensive Movie &amp; TV Hub</h4>
              <p>Explore hundreds of thousands of films and shows with full cast credits, directors, trailers, release
                calendars, and audience ratings powered by the TMDB API.</p>
            </div>
            <div class="feature-box">
              <h4>📰 02. The Cinephile Journal</h4>
              <p>Curated editorial blogs, cinema retrospectives, behind-the-scenes deep-dives, and unbiased reviews
                penned with deep respect for celluloid history.</p>
            </div>
            <div class="feature-box">
              <h4>🎟️ 03. Interactive Trivia Arena</h4>
              <p>Test your film knowledge across diverse cinematic eras. Earn Director Passes, unlock rare virtual
                badges, and claim your place on global leaderboards.</p>
            </div>
            <div class="feature-box">
              <h4>🔍 04. Intelligent Cinema Discovery</h4>
              <p>Filter by genre, director, era, and runtime to discover overlooked masterpieces and indie gems
                alongside box-office blockbusters.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 2: Mission & Editorial Principles -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-teal">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Editorial Integrity</span>
            <h2 class="card-title">Our Mission &amp; Editorial Standards</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            In an era of generic aggregators, SceneTale champions authentic cinema appreciation. We believe every great
            movie is a convergence of vision, writing, cinematography, sound design, and performance.
          </p>
          <ul class="styled-list">
            <li>
              <strong>Authenticity &amp; Independent Voice:</strong> Our editorial reviews and trivia questions are
              researched and crafted with dedication to factual accuracy and genuine critical depth.
            </li>
            <li>
              <strong>Catering to All Moviegoers:</strong> From casual Friday night watchers to seasoned film school
              students, our guides are accessible, educational, and engaging.
            </li>
            <li>
              <strong>Diversity in Storytelling:</strong> We actively highlight global cinema, indie filmmakers, foreign
              language masterworks, and underrepresented voices in the film industry.
            </li>
          </ul>
        </div>
      </section>

      <!-- Section 3: Data Sources & Technology -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-amber">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
              <line x1="7" y1="2" x2="7" y2="22"></line>
              <line x1="17" y1="2" x2="17" y2="22"></line>
              <line x1="2" y1="12" x2="22" y2="12"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Data Architecture</span>
            <h2 class="card-title">Technology &amp; Third-Party Data Sources</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            SceneTale is engineered with modern web architecture to guarantee lightning-fast load times, seamless
            navigation, and reliable data synchronization:
          </p>
          <div class="highlight-callout">
            <strong>TMDB API Integration:</strong> Film metadata, release dates, actor filmographies, and posters are
            updated in real-time using The Movie Database (TMDB) API. (This product uses the TMDB API but is not
            endorsed or certified by TMDB).
          </div>
          <p>
            Our interactive trivia engine evaluates answers with millisecond precision, while our single-page
            application structure ensures zero page flickering when navigating between the movie hub and cinephile
            quizzes.
          </p>
        </div>
      </section>

      <!-- Section 4: Advertising & Community Transparency -->
      <section class="policy-card featured-policy-card">
        <div class="card-header-row">
          <div class="card-icon-box icon-gold">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag tag-gold">Sustainability</span>
            <h2 class="card-title">Advertising Transparency &amp; User Experience</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            We believe an online platform can remain 100% free for users without compromising on design aesthetics or
            reader dignity:
          </p>
          <ul class="styled-list">
            <li>
              <strong>Clean, Family-Friendly Ads:</strong> We partner with trusted advertising networks such as
              <strong>Google AdSense</strong> to support server costs, trivia development, and editorial research.
            </li>
            <li>
              <strong>No Disruptive Overlays:</strong> We reject intrusive pop-ups, auto-playing video noise, and
              obstructive overlays that distract from the cinematic experience.
            </li>
            <li>
              <strong>Privacy Protection:</strong> We adhere strictly to GDPR, CCPA, and Google AdSense cookie
              guidelines. Users maintain complete autonomy over cookie preferences.
            </li>
          </ul>
        </div>
      </section>

      <!-- Section 5: Platform Stats Bar -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-emerald">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">By The Numbers</span>
            <h2 class="card-title">SceneTale at a Glance</h2>
          </div>
        </div>
        <div class="card-body">
          <div class="grid-two-col">
            <div class="feature-box">
              <h4>🎞️ 500K+ Titles Indexed</h4>
              <p>Historical classics, Oscar winners, international indie films, and upcoming theatrical premieres.</p>
            </div>
            <div class="feature-box">
              <h4>🎯 1,000+ Trivia Questions</h4>
              <p>Handcrafted trivia spanning directors, quotes, cinematography, soundtracks, and award history.</p>
            </div>
            <div class="feature-box">
              <h4>📰 50+ Editorial Journals</h4>
              <p>Comprehensive reviews, filmmaker profiles, and retrospectives published for true cinephiles.</p>
            </div>
            <div class="feature-box">
              <h4>⚡ 100% Free Access</h4>
              <p>Explore movie data, read cinema blogs, and participate in trivia with no paywalls.</p>
            </div>
          </div>
        </div>
      </section>

    </div>

    <!-- ═══════════════ BOTTOM CTA BANNER ═══════════════ -->
    <section class="bottom-trust-card">
      <div class="trust-content">
        <h3>Begin Your Cinematic Journey</h3>
        <p>Dive into real-time movie discoveries, challenge your film knowledge, or reach out to our team.</p>
        <div class="trust-actions">
          <a routerLink="/" class="btn-primary-pill">Explore Movies</a>
          <a routerLink="/quiz" class="btn-secondary-pill">Play Daily Trivia &rarr;</a>
          <a routerLink="/contact" class="btn-secondary-pill">Contact Us &rarr;</a>
        </div>
      </div>
    </section>

  </div>
</div>

<app-footer></app-footer>